import {
  DESKTOP_MIN_WIDTH,
  OBJECT_DEFAULT,
  REGISTER_EMAIL_NAME,
} from 'constants';
import { Fade } from 'helpers/animation';
import useFormInput from 'hooks/form/useFormInput';
import useLang from 'hooks/useLang';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { checkFormInputValidation } from '../../func';
import AccountExistedInform from '../AccountExistedInform';

const FormInputValidation = ({
  ref,
  field,
  information,
  validation,
  setInformation,
  setValidation,
  isEmailExisted,
  setEmailExisted,
  onForgotPassword,
  rootStyles = OBJECT_DEFAULT,
  // Additive, optional. Default is a no-op, so the other current caller
  // (this component's email/username field in FormRegister/LogInDetails)
  // sees byte-identical behavior. When true, handleOnBlur becomes a
  // complete no-op -- used by the PayTo email field so blur never clears
  // an existing (possibly async validatePayId-derived) message.
  suppressBlurValidation = false,
  // DEV-1401: additive, optional, default false. When true (only the PayTo
  // PayID email field passes true, and only while its bank-validation
  // failure row is shown), the inline feedback text renders empty instead
  // of the mapped message -- isInvalid/styling and validation truthiness
  // are unaffected, so blocking/gating behavior stays exactly as before.
  suppressMessage = false,
}) => {
  const { t } = useLang();

  const {
    inputRef,
    resetFormInput,
    setInformationFormInput,
    setValidationFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const isShowAccountExistedInform =
    isEmailExisted && field.name === REGISTER_EMAIL_NAME;

  const handleEmailOnChange = (name, value) => {
    const isEmailField = name === REGISTER_EMAIL_NAME;

    if (isEmailField && isEmailExisted) {
      setEmailExisted(false);
    }

    return value;
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;
    let valueTrim = getValueTrim(value);

    valueTrim = handleEmailOnChange(name, valueTrim);

    if (!valueTrim) {
      // resetFormInput({
      //   information,
      //   setInformation,
      //   validation,
      //   setValidation,
      // });
      let newInformation = setInformationFormInput({
        value: '',
        information,
        setInformation,
      });
      setValidationFormInput({
        validation,
        setValidation,
        checkFormInputValidation: () =>
          checkFormInputValidation(
            newInformation[name],
            field,
            newInformation,
            validation,
          ),
      });
      return;
    }

    let newInformation = setInformationFormInput({
      value: valueTrim,
      information,
      setInformation,
    });

    setValidationFormInput({
      validation,
      setValidation,
      checkFormInputValidation: () =>
        checkFormInputValidation(
          newInformation[name],
          field,
          newInformation,
          validation,
        ),
    });
  };

  const handleOnBlur = (e) => {
    if (suppressBlurValidation) return;

    handleOnChange(e);
  };

  return (
    <FormGroupStyled
      $isShowAccountExistedInform={isShowAccountExistedInform}
      style={rootStyles || {}}
    >
      <FormLabel />
      <FormControl
        ref={ref}
        type={field.type}
        name={field.name}
        value={information ? information[field.name] : ''}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        isInvalid={validation[field.name]}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        readOnly={field?.isReadOnly}
      />
      <FormControlFeedback
        value={suppressMessage ? '' : t(validation[field.name])}
      />
      {isShowAccountExistedInform && (
        <AccountExistedInform onForgotPassword={onForgotPassword} />
      )}
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: ${(props) =>
    props.$isShowAccountExistedInform ? '16px' : '32px'};
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    border: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputValidation;
